import { FC, useMemo } from 'react';

import { ColumnTable, Entry } from '@/components/ColumnTable';
import { RelationName } from '@/components/RelationName';
import { useSnapshot } from '@/hooks/dbtStrategy/useSnapshot';
import { useCommonLineageEntries } from '@/hooks/useCommonLineageEntries';
import { PanelDetails, ResourceDetailsParams } from './panelDetailTypes';

export const useSnapshotDetailsPanel = ({
  uniqueId,
  projectName,
}: ResourceDetailsParams): PanelDetails => {
  const snapshotQuery = useSnapshot(uniqueId);

  const commonEntries = useCommonLineageEntries(projectName);

  return useMemo(() => {
    const snapshot = snapshotQuery.data.applied;

    if (!snapshot) {
      return { ...snapshotQuery, rows: null, description: null };
    }

    const rows: Entry[] = [
      ...commonEntries,
      {
        key: 'Relation',
        data: <RelationName relation={snapshot} />,
        disableTooltip: true,
      },
      {
        key: 'tags',
        data: JSON.stringify(snapshot.tags || []),
      },
      {
        key: 'Package',
        data: uniqueId.packageName,
      },
    ];

    return { ...snapshotQuery, rows, description: snapshot.description };
  }, [snapshotQuery, commonEntries, uniqueId.packageName]);
};

export const SnapshotDetailsTable: FC<ResourceDetailsParams> = ({
  className,
  children,
  compact,
  ...props
}) => {
  const snapshotDetails = useSnapshotDetailsPanel(props);

  return (
    <div className={className}>
      {children?.(snapshotDetails)}
      <div className="my-2">
        <ColumnTable
          tableEntries={snapshotDetails.rows}
          compact={compact}
          isLoading={snapshotDetails.fetching}
        />
      </div>
    </div>
  );
};
